import React from "react";
import {Modal, Tag, Tooltip} from "antd";

const SupplierDetailsPopup = ({Visible, onClose, supplierDetails}) => {
    const handlePopupCancel = () => {
        onClose();
    };

    const Field = ({label, children, span = 1, fullWidth = false}) => (
        <div className={fullWidth ? "sm:col-span-2 lg:col-span-3" : span === 2 ? "sm:col-span-2" : ""}>
            <div className="text-[11px] uppercase tracking-wider text-[#7A849A] mb-1">{label}</div>
            <div className="text-[14px] text-[#0B1220] break-words">
                {children || <span className="text-[#A5ADBF]">—</span>}
            </div>
        </div>
    );

    const BoolTag = ({on}) => (
        <Tag className="!m-0" color={on ? "green" : "default"}>
            {on ? "Yes" : "No"}
        </Tag>
    );

    const statusTags = supplierDetails ? [
        {label: "Vetted", on: supplierDetails.isVetted, color: "green"},
        {label: "Recovery supplier", on: supplierDetails.isRecoverySupplier, color: "geekblue"},
        {label: "Invoice supplier", on: supplierDetails.isCompanyInvoiceSupplier, color: "blue"},
        {label: "Marcas supplier", on: supplierDetails.isMarcasSupplier, color: "purple"},
        {label: "Whitelisted", on: supplierDetails.isFromWhitelist, color: "cyan"},
        {label: "Allows rounding", on: supplierDetails.permitInvoiceRounding, color: "gold"},
    ].filter(t => t.on) : [];

    const recoveryTypes = supplierDetails?.recoveryServiceTypes ?? [];
    const invoiceEmails = supplierDetails?.companyInvoiceEmails ?? [];
    const allowedCurrencies = supplierDetails?.companyAllowedCurrencies ?? [];

    return (
        <Modal
            title="Supplier details"
            open={Visible}
            onCancel={handlePopupCancel}
            footer={null}
            width={1100}
        >
            {supplierDetails && (
                <div className="space-y-4">
                    {/* Hero */}
                    <div className="bg-[#F7F8FB] border border-[#E4E7EE] rounded-[12px] p-4">
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <Tooltip title={supplierDetails.name}>
                                    <div className="text-[18px] font-semibold text-[#0B1220] truncate">
                                        {supplierDetails.name || "—"}
                                    </div>
                                </Tooltip>
                                {supplierDetails.Address && (
                                    <div className="text-[13px] text-[#7A849A] mt-1">{supplierDetails.Address}</div>
                                )}
                                {supplierDetails.id && (
                                    <div className="cell-mono text-[12px] text-[#7A849A] mt-1">#{supplierDetails.id}</div>
                                )}
                            </div>
                            {statusTags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {statusTags.map((t) => (
                                        <Tag key={t.label} color={t.color} className="!m-0">{t.label}</Tag>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
                        <Field label="Type">
                            {supplierDetails.type?.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {supplierDetails.type.map((t, i) => (
                                        <Tag key={i} className="!m-0 capitalize">{t}</Tag>
                                    ))}
                                </div>
                            ) : null}
                        </Field>
                        <Field label="Country">{supplierDetails.address?.countryName}</Field>
                        <Field label="Telephone">{supplierDetails.address?.telephone}</Field>

                        <Field label="Email">{supplierDetails.email}</Field>
                        <Field label="Correspondence email">{supplierDetails.correspondenceEmail}</Field>
                        <Field label="Payment term days">{supplierDetails.paymentTermDays}</Field>

                        <Field label="Allowed currencies" span={2}>
                            {allowedCurrencies.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {allowedCurrencies.map((c, i) => (
                                        <Tag key={i} className="!m-0 cell-mono">{c.value}</Tag>
                                    ))}
                                </div>
                            ) : null}
                        </Field>
                        <Field label="Purchase orders LTM">
                            <span className="cell-amt">{supplierDetails.purchasesCount ?? 0}</span>
                        </Field>

                        <Field label="Invoice emails" span={2}>
                            {invoiceEmails.length > 0 ? (
                                <div className="flex flex-wrap gap-x-2 gap-y-1">
                                    {invoiceEmails.map((e, i) => (
                                        <span key={i} className="cell-mono">
                                            {e.value}{i < invoiceEmails.length - 1 ? "," : ""}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </Field>
                        <Field label="Recovery service types">
                            {recoveryTypes.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {recoveryTypes.map((r, i) => (
                                        <Tag key={i} className="!m-0">{r.value}</Tag>
                                    ))}
                                </div>
                            ) : null}
                        </Field>
                    </div>

                    {/* Status row — always visible so missing flags are obvious */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 border-t border-[#F1F3F7] pt-4">
                        <Field label="Vetted"><BoolTag on={supplierDetails.isVetted}/></Field>
                        <Field label="Recovery"><BoolTag on={supplierDetails.isRecoverySupplier}/></Field>
                        <Field label="Invoice supplier"><BoolTag on={supplierDetails.isCompanyInvoiceSupplier}/></Field>
                        <Field label="Marcas"><BoolTag on={supplierDetails.isMarcasSupplier}/></Field>
                        <Field label="Whitelisted"><BoolTag on={supplierDetails.isFromWhitelist}/></Field>
                        <Field label="Rounding"><BoolTag on={supplierDetails.permitInvoiceRounding}/></Field>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default SupplierDetailsPopup;
